import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTenant } from "../../context/TenantContext";
import {
  getStorefrontThemeConfig,
  STOREFRONT_TEMPLATE_IDS,
} from "../../utils/storefrontTheme";
import {
  FaFacebook,
  FaInstagram,
  FaTwitter,
  FaLinkedin,
} from "react-icons/fa";
import { FiMail, FiMapPin, FiPhone, FiSend, FiClock } from "react-icons/fi";
import toast from "react-hot-toast";
import "../../styles/footer.css";

const Footer = () => {
  const { tenant, settings, loading } = useTenant();
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [isMobileViewport, setIsMobileViewport] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= 768,
  );

  useEffect(() => {
    const updateViewport = () => setIsMobileViewport(window.innerWidth <= 768);
    window.addEventListener("resize", updateViewport);
    return () => window.removeEventListener("resize", updateViewport);
  }, []);

  const handleNewsletter = async (e) => {
    e.preventDefault();
    if (!newsletterEmail) {
      toast.error("Please enter your email", { duration: 2000 });
      return;
    }
    toast.success("Subscribed to newsletter!", { duration: 2000 });
    setNewsletterEmail("");
  };

  const currentYear = new Date().getFullYear();

  const handleTooraClick = () => {
    window.open("https://www.tooraflex.co.za", "_blank");
  };

  const getFullAddress = () => {
    const addressParts = [];
    if (tenant?.AddressLine1) addressParts.push(tenant.AddressLine1);
    if (tenant?.AddressLine2) addressParts.push(tenant.AddressLine2);
    if (tenant?.City) addressParts.push(tenant.City);
    if (tenant?.State) addressParts.push(tenant.State);
    if (tenant?.PostalCode) addressParts.push(tenant.PostalCode);
    if (tenant?.Country) addressParts.push(tenant.Country);
    return addressParts.join(", ");
  };

  const getFormattedBusinessHours = () => {
    const hours = tenant?.BusinessHours;
    if (!hours) return null;
    try {
      const parsedHours = typeof hours === "string" ? JSON.parse(hours) : hours;
      const days = ["monday", "tuesday", "wednesday", "thursday", "friday"];
      let weekdayHours = null;
      let sameForAllWeekdays = true;
      for (let i = 1; i < days.length; i++) {
        const prev = parsedHours[days[i - 1]];
        const curr = parsedHours[days[i]];
        if (prev?.open !== curr?.open || prev?.close !== curr?.close) {
          sameForAllWeekdays = false;
          break;
        }
      }
      if (sameForAllWeekdays && parsedHours.monday?.enabled) {
        weekdayHours = `${parsedHours.monday.open}-${parsedHours.monday.close}`;
      }
      const result = [];
      if (weekdayHours) {
        result.push(`Mon-Fri ${weekdayHours}`);
      } else {
        const weekdays = days.filter((d) => parsedHours[d]?.enabled);
        if (weekdays.length > 0) {
          result.push(
            weekdays
              .map((d) => {
                const day = parsedHours[d];
                return `${d.charAt(0).toUpperCase() + d.slice(1, 3)} ${day.open}-${day.close}`;
              })
              .join(", "),
          );
        }
      }
      if (parsedHours.saturday?.enabled) {
        result.push(
          `Sat ${parsedHours.saturday.open}-${parsedHours.saturday.close}`,
        );
      }
      if (parsedHours.sunday?.enabled) {
        result.push(
          `Sun ${parsedHours.sunday.open}-${parsedHours.sunday.close}`,
        );
      }
      return result.join(" • ");
    } catch (e) {
      return null;
    }
  };

  const formatTelLink = (phone) => {
    if (!phone) return "";
    return phone.replace(/[\s\-\(\)]/g, "");
  };

  const hexToRgb = (hex) => {
    if (!hex) return { r: 44, g: 125, b: 160 };
    let c = hex.replace("#", "");
    if (c.length === 3) {
      c = c
        .split("")
        .map((ch) => ch + ch)
        .join("");
    }
    const num = parseInt(c, 16);
    return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
  };

  const getLuminance = (hex) => {
    const { r, g, b } = hexToRgb(hex);
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  };

  const themeConfig = getStorefrontThemeConfig(
    settings?.storefrontTemplate,
    {
      ...(settings?.storefrontTemplate === "classic"
        ? {
            primaryColor: tenant?.PrimaryColor,
            accentColor: tenant?.SecondaryColor,
          }
        : {}),
      ...(settings?.storefrontThemeConfig || {}),
    },
  );
  const footerContent = themeConfig.storefrontContent.footer;
  const mobileLayout = themeConfig.storefrontContent.mobileLayout;
  const mobileFooterColumns = mobileLayout.footerColumns;
  const configuredNavigation = themeConfig.storefrontContent.navigation;
  const quickLinks = [
    { path: "/", label: "Home" },
    { path: "/shop", label: "Shop" },
    { path: "/about", label: "About Us" },
    { path: "/contact", label: "Contact" },
    { path: "/faq", label: "FAQ" },
    { path: "/reviews", label: "Customer Reviews" },
  ]
    .map((link) => ({
      ...link,
      label:
        configuredNavigation.find((item) => item.path === link.path)?.label ||
        link.label,
    }))
    .filter(
      (link) =>
        !configuredNavigation.some(
          (item) => item.path === link.path && item.enabled === false,
        ),
    )
    .concat(
      configuredNavigation
        .filter(
          (item) =>
            item.enabled !== false &&
            /^\/pages\/[a-z0-9]+(?:-[a-z0-9]+)*$/.test(item.path),
        )
        .map(({ path, label }) => ({ path, label })),
    );
  const footerStyle = themeConfig.footerStyle;
  const customPrimaryColor = /^#[0-9a-fA-F]{6}$/.test(
    themeConfig.primaryColor || "",
  )
    ? themeConfig.primaryColor
    : tenant?.PrimaryColor || "#2c7da0";
  const footerBgColor =
    footerStyle === "dark"
      ? "#17212b"
      : footerStyle === "light"
        ? "#ffffff"
        : /^#[0-9a-fA-F]{6}$/.test(themeConfig.primaryColor || "")
          ? themeConfig.primaryColor
          : tenant?.HeaderFooterColor || customPrimaryColor;
  const effectiveFooterBgColor =
    footerStyle === "brand" &&
    settings?.storefrontTemplate === "classic" &&
    !settings?.storefrontThemeConfig?.primaryColor
      ? tenant?.HeaderFooterColor || footerBgColor
      : footerBgColor;
  const isLightBg = getLuminance(effectiveFooterBgColor) > 0.6;

  const textPrimary = isLightBg ? "#111827" : "#ffffff";
  const textSecondary = isLightBg
    ? "rgba(17, 24, 39, 0.75)"
    : "rgba(255, 255, 255, 0.8)";
  const textMuted = isLightBg
    ? "rgba(17, 24, 39, 0.6)"
    : "rgba(255, 255, 255, 0.65)";
  const dividerColor = isLightBg
    ? "rgba(0, 0, 0, 0.1)"
    : "rgba(255, 255, 255, 0.15)";
  const newsletterBtnBg = isLightBg ? "#111827" : "#ffffff";
  const newsletterBtnText = isLightBg ? "#ffffff" : effectiveFooterBgColor;
  const newsletterInputBg = isLightBg ? "#ffffff" : "rgba(255, 255, 255, 0.95)";
  const socialIconBg = isLightBg
    ? "rgba(0, 0, 0, 0.06)"
    : "rgba(255, 255, 255, 0.12)";
  const socialIconHoverBg = isLightBg ? "#111827" : "#ffffff";
  const socialIconHoverColor = isLightBg ? "#ffffff" : effectiveFooterBgColor;

  const businessHours = getFormattedBusinessHours();

  const hasSocialLinks =
    tenant?.SocialFacebook ||
    tenant?.SocialInstagram ||
    tenant?.SocialTwitter ||
    tenant?.SocialLinkedIn;

  const paymentMethods = [
    {
      name: "Visa",
      image:
        "https://1000logos.net/wp-content/uploads/2017/06/VISA-Logo-1992.png",
    },
    {
      name: "Mastercard",
      image:
        "https://upload.wikimedia.org/wikipedia/commons/thumb/2/2a/Mastercard-logo.svg/1280px-Mastercard-logo.svg.png",
    },
    {
      name: "PayPal",
      image:
        "https://www.paypalobjects.com/webstatic/mktg/logo/pp_cc_mark_37x23.jpg",
    },
    {
      name: "PayFast",
      image:
        "https://dialanerd.co.za/wp-content/uploads/2016/09/PayFast-Logo-Colour.png",
    },
    {
      name: "Ozow",
      image: "https://dash.ozow.com/images/ozow-logo.svg",
    },
    {
      name: "Yoco",
      image:
        "https://a.storyblok.com/f/111633/2400x1260/81bddfa73a/yoco-logo-blue.jpg",
    },
  ];

  const footerCssVars = {
    "--footer-bg": effectiveFooterBgColor,
    "--footer-text": textPrimary,
    "--footer-text-secondary": textSecondary,
    "--footer-text-muted": textMuted,
    "--footer-divider": dividerColor,
    "--newsletter-btn-bg": newsletterBtnBg,
    "--newsletter-btn-text": newsletterBtnText,
    "--newsletter-input-bg": newsletterInputBg,
    "--social-icon-bg": socialIconBg,
    "--social-icon-hover-bg": socialIconHoverBg,
    "--social-icon-hover-color": socialIconHoverColor,
    "--footer-accent": textPrimary,
    backgroundColor: effectiveFooterBgColor,
  };

  if (loading) return null;

  const footerTemplate = STOREFRONT_TEMPLATE_IDS.includes(
    settings?.storefrontTemplate,
  )
    ? settings.storefrontTemplate
    : "classic";

  return (
    <footer
      className={`toora-footer storefront-footer-${footerTemplate}`}
      style={footerCssVars}
      data-mobile-columns={mobileFooterColumns === 2 ? "two" : "one"}
      data-mobile-sections={mobileLayout.footerStyle || "open"}
    >
      <div className="toora-footer-container">
        {/* ================= TOP GRID ================= */}
        <div className="toora-footer-grid">
          {/* Column 1: Quick Links */}
          <details
            className="toora-footer-section"
            open={!isMobileViewport || mobileLayout.footerStyle !== "accordion"}
          >
            <summary><h3>{footerContent.quickLinksTitle}</h3></summary>
            <div className="toora-footer-section-content">
              <ul>
                {quickLinks.map((link) => (
                  <li key={link.path}>
                    <Link to={link.path}>{link.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          </details>

          {/* Column 2: Support */}
          <details
            className="toora-footer-section"
            open={!isMobileViewport || mobileLayout.footerStyle !== "accordion"}
          >
            <summary><h3>{footerContent.supportTitle}</h3></summary>
            <div className="toora-footer-section-content">
              <ul>
                <li>
                  <Link to="/contact">Contact Support</Link>
                </li>
                <li>
                  <Link to="/track-order">Track Order</Link>
                </li>
              </ul>
            </div>
          </details>

          {/* Column 3: Contact Us */}
          <details
            className="toora-footer-section"
            open={!isMobileViewport || mobileLayout.footerStyle !== "accordion"}
          >
            <summary><h3>{footerContent.contactTitle}</h3></summary>
            <div className="toora-footer-section-content">
              {getFullAddress() && (
                <div className="toora-contact-item">
                  <FiMapPin className="toora-contact-icon" />
                  <span>{getFullAddress()}</span>
                </div>
              )}
              {tenant?.BusinessPhone && (
                <div className="toora-contact-item">
                  <FiPhone className="toora-contact-icon" />
                  <a href={`tel:${formatTelLink(tenant.BusinessPhone)}`}>
                    {tenant.BusinessPhone}
                  </a>
                </div>
              )}
              {tenant?.BusinessEmail && (
                <div className="toora-contact-item">
                  <FiMail className="toora-contact-icon" />
                  <a href={`mailto:${tenant.BusinessEmail}`}>
                    {tenant.BusinessEmail}
                  </a>
                </div>
              )}
              {businessHours && (
                <div className="toora-contact-item">
                  <FiClock className="toora-contact-icon" />
                  <span>{businessHours}</span>
                </div>
              )}
            </div>
          </details>

          {/* Column 4: Newsletter & Social */}
          <details
            className="toora-footer-section"
            open={!isMobileViewport || mobileLayout.footerStyle !== "accordion"}
          >
            <summary><h3>{footerContent.newsletterTitle}</h3></summary>
            <div className="toora-footer-section-content">
              <p className="toora-newsletter-text">
                {footerContent.newsletterText}
              </p>
              <div className="toora-newsletter-form">
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  className="toora-newsletter-input"
                  onKeyPress={(e) => {
                    if (e.key === "Enter") handleNewsletter(e);
                  }}
                />
                <button
                  onClick={handleNewsletter}
                  className="toora-newsletter-btn"
                >
                  <FiSend /> Subscribe
                </button>
              </div>

              {footerContent.showSocial && hasSocialLinks && (
                <div className="toora-social-section">
                  <h3>Follow Us</h3>
                  <div className="toora-social-icons">
                    {tenant?.SocialFacebook && (
                      <a
                        href={tenant.SocialFacebook}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="toora-social-icon"
                        aria-label="Facebook"
                      >
                        <FaFacebook />
                      </a>
                    )}
                    {tenant?.SocialInstagram && (
                      <a
                        href={tenant.SocialInstagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="toora-social-icon"
                        aria-label="Instagram"
                      >
                        <FaInstagram />
                      </a>
                    )}
                    {tenant?.SocialTwitter && (
                      <a
                        href={tenant.SocialTwitter}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="toora-social-icon"
                        aria-label="Twitter"
                      >
                        <FaTwitter />
                      </a>
                    )}
                    {tenant?.SocialLinkedIn && (
                      <a
                        href={tenant.SocialLinkedIn}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="toora-social-icon"
                        aria-label="LinkedIn"
                      >
                        <FaLinkedin />
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          </details>
        </div>

        {/* ================= POLICIES ================= */}
        {footerContent.showPolicies && (
          <div className="toora-policies-section">
            <div className="toora-policies-links">
              <Link to="/privacy">Privacy Policy</Link>
              <span className="toora-policies-separator">•</span>
              <Link to="/terms">Terms &amp; Conditions</Link>
              <span className="toora-policies-separator">•</span>
              <Link to="/shipping">Shipping Policy</Link>
              <span className="toora-policies-separator">•</span>
              <Link to="/returns">Returns Policy</Link>
              <span className="toora-policies-separator">•</span>
              <Link to="/cancellation-policy">Cancellation Policy</Link>
              <span className="toora-policies-separator">•</span>
              <Link to="/cookies">Cookies Policy</Link>
            </div>
          </div>
        )}

        {/* ================= BOTTOM BAR ================= */}
        <div className="toora-footer-bottom">
          <div className="toora-footer-bottom-left">
            <p>
              {footerContent.copyright ||
                `© ${currentYear} ${tenant?.BusinessName || "Store Name"}. All rights reserved.`}
            </p>
          </div>

          <div className="toora-footer-bottom-center">
            {footerContent.showPayments && paymentMethods.map((method) => (
              <div className="toora-payment-badge" key={method.name}>
                <img
                  src={method.image}
                  alt={method.name}
                  title={method.name}
                  className="toora-payment-icon"
                  loading="lazy"
                />
              </div>
            ))}
          </div>

          <div className="toora-footer-bottom-right">
            <div className="toora-credit" onClick={handleTooraClick}>
              <span>Site by</span>
              <span className="toora">Toora</span>
              <span className="flex">Flex</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
